import { useEffect, useState } from "react";
import { toast } from "sonner";
import { RotateCcw, Save, Target } from "lucide-react";
import { api } from "@/lib/api";

/**
 * iter86.31.7 — Template Scoring & Baselines (Product Ops → Templates).
 * Pick a shape → its scoring fields render → edit → save. New tournaments
 * adopt the saved baseline (request > saved > codebase > defaults chain).
 * Golf scoring is engine-owned + per-tournament editable in the Admin
 * Panel, so golf shapes aren't listed here.
 */
const SHAPES = [
    ["world_cup_api", "Soccer Tournament — World Cup"],
    ["world_cup_ko_api", "Soccer KO — World Cup"],
    ["premier_league_api", "Soccer Season — Premier League"],
    ["la_liga_api", "Soccer Season — La Liga"],
    ["champions_league_api", "Soccer Season — Champions League"],
    ["club_season_api", "Soccer Season — Dynamic leagues"],
];

const GROUPS = [
    { title: "Match points", keys: [["win_points", "Win"], ["draw_points", "Draw"], ["loss_points", "Loss"]], always: true },
    { title: "Stage-reach points", keys: [["reach_r64_points", "Reach R64"], ["reach_r32_points", "Reach R32"], ["reach_r16_points", "Reach R16"], ["reach_qf_points", "Reach QF"], ["reach_sf_points", "Reach SF"], ["reach_final_points", "Reach Final"], ["bronze_points", "Bronze"], ["champion_points", "Champion"]] },
    { title: "Knockout bonus pick (decay)", keys: [["bonus_pre_knockout", "Pre-KO"], ["bonus_after_r64", "After R64"], ["bonus_after_r32", "After R32"], ["bonus_after_r16", "After R16"], ["bonus_after_qf", "After QF"], ["bonus_after_sf", "After SF"]] },
];

export default function TemplateScoringCard() {
    const [shape, setShape] = useState("");
    const [data, setData] = useState(null);
    const [draft, setDraft] = useState({});
    const [busy, setBusy] = useState(false);

    const load = async (key) => {
        if (!key) { setData(null); return; }
        try {
            const d = await api.siteGetTemplateScoring(key);
            setData(d);
            setDraft(d.scoring || {});
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to load scoring");
        }
    };
    useEffect(() => { load(shape); }, [shape]); // eslint-disable-line react-hooks/exhaustive-deps

    const isSeason = data?.format === "round_robin";

    const save = async () => {
        setBusy(true);
        try {
            const scoring = { ...draft };
            for (const k of Object.keys(scoring)) {
                if (k !== "cumulative_advancement") scoring[k] = Number(scoring[k]) || 0;
            }
            await api.sitePutTemplateScoring(shape, scoring);
            toast.success("Scoring baseline saved — new tournaments adopt it");
            load(shape);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save failed");
        }
        setBusy(false);
    };

    const reset = async () => {
        if (!window.confirm("Revert this shape to its codebase scoring defaults?")) return;
        setBusy(true);
        try {
            await api.siteResetTemplateScoring(shape);
            toast.success("Reverted to codebase defaults");
            load(shape);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Reset failed");
        }
        setBusy(false);
    };

    return (
        <div className="brutal-card p-4 space-y-3" data-testid="template-scoring-card">
            <div>
                <h3 className="font-display font-extrabold text-sm uppercase flex items-center gap-2">
                    <Target className="w-4 h-4 text-cyber" /> Template Scoring & Baselines
                </h3>
                <p className="text-[11px] text-zinc-500 mt-0.5">
                    Editable per-shape scoring baseline. New tournaments adopt it on creation
                    (rivalry admins can still tune their own copy afterwards). Golf scoring is
                    engine-owned and edited per tournament in the Admin Panel.
                </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
                <select data-testid="scoring-shape-select" value={shape}
                        onChange={(e) => setShape(e.target.value)}
                        className="brutal-input text-xs max-w-[300px]">
                    <option value="">— pick a shape —</option>
                    {SHAPES.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                </select>
                {data && (
                    <span data-testid="scoring-source-pill"
                          className={`pill text-[9px] px-2 py-0.5 border ${data.source === "saved" ? "bg-cyber/15 border-cyber text-cyber" : "bg-zinc-900 border-zinc-700 text-zinc-400"}`}>
                        {data.source === "saved" ? "SAVED OVERRIDE" : "CODEBASE DEFAULT"}
                    </span>
                )}
            </div>
            {data && (
                <>
                    {GROUPS.filter(g => g.always || !isSeason).map(g => (
                        <div key={g.title}>
                            <p className="text-[10px] uppercase font-bold text-zinc-500 mb-1.5">{g.title}</p>
                            <div className="flex flex-wrap gap-2">
                                {g.keys.map(([k, label]) => (
                                    <label key={k} className="text-[10px] text-zinc-400">
                                        <span className="block mb-0.5">{label}</span>
                                        <input data-testid={`scoring-field-${k}`} type="number"
                                            value={draft[k] ?? 0}
                                            onChange={(e) => setDraft(p => ({ ...p, [k]: e.target.value }))}
                                            className="brutal-input w-20 font-mono text-xs" />
                                    </label>
                                ))}
                            </div>
                        </div>
                    ))}
                    {!isSeason && (
                        <label className="flex items-center gap-2 text-xs text-zinc-400" data-testid="scoring-cumulative-toggle">
                            <input type="checkbox" checked={!!draft.cumulative_advancement}
                                onChange={(e) => setDraft(p => ({ ...p, cumulative_advancement: e.target.checked }))} />
                            Cumulative advancement (reach points stack stage over stage)
                        </label>
                    )}
                    <div className="flex items-center gap-2 pt-1">
                        <button data-testid="scoring-save-btn" onClick={save} disabled={busy}
                                className="brutal-btn-primary text-xs flex items-center gap-1.5 disabled:opacity-40">
                            <Save className="w-3.5 h-3.5" /> Save baseline
                        </button>
                        {data.source === "saved" && (
                            <button data-testid="scoring-reset-btn" onClick={reset} disabled={busy}
                                    className="brutal-btn-ghost text-xs flex items-center gap-1.5 disabled:opacity-40">
                                <RotateCcw className="w-3.5 h-3.5" /> Revert to codebase default
                            </button>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}
